import { z } from "zod";
import { LessonId } from "./room.ts";

/**
 * Состав комнаты: `GET /v1/lessons/:lessonId/participants`.
 *
 * Отдельным файлом от `mic.ts` по той же границе, по которой тот отделён от
 * `token.ts`: там распоряжение микрофоном внутри комнаты, здесь ответ на
 * вопрос «кто на этом занятии вообще есть». Читателю удобнее по этой границе,
 * а не по числу строк.
 *
 * **Зачем ручка нужна.** У списка участников на экране ДВА источника состава и
 * ни одного готового (`docs/group-room-design.md`, раздел 14). Присутствие
 * документа знает только тех, кто пришёл, а строка «Саша, не в комнате»
 * требует полного состава. `student_group_member` на эту роль не годится: он
 * расходится с комнатой у ученика, добавленного в группу после заведения
 * расписания. Состав комнаты это ученики её уроков, и знает их только сервер.
 *
 * ⚠️ **Признака «в комнате» здесь нет и заводить его нельзя.** Кто прямо
 * сейчас в комнате, знает присутствие документа на клиенте, и знает точнее:
 * сервер про открытые вкладки не знает вовсе. Поле в ответе было бы вторым
 * ответом на тот же вопрос, отстающим от первого на время запроса, и экран
 * рисовал бы «не в комнате» под человеком, который рисует на доске.
 */

/** Один человек в составе комнаты. */
export const RoomParticipant = z.object({
  userId: z.string().uuid(),
  /**
   * Урок ЭТОГО ученика в комнате, он же имя документа с его ответами
   * (`answersDocIdOf`).
   *
   * ⚠️ **Нужен ровно затем, чтобы преподаватель мог сложить агрегат ответов.**
   * Ответы каждого лежат в своём документе, названном его уроком, и связать
   * документ с человеком больше нечем: `userId` имени документа не даёт, а
   * гадать по порядку списка нельзя. Заодно этим же полем выписывается пропуск
   * на подписку (`POST /v1/lessons/:id/board-ticket` с телом `{ doc }`).
   *
   * ⚠️ **Поле необязательное**, как соседние поля этой пачки: новый бандл против
   * старого API не имеет права уронить разбор состава целиком. Отсутствие
   * означает «сервер старше», и агрегат тогда просто не собирается.
   */
  lessonId: LessonId.optional(),
  /**
   * ⚠️ **Подпись, а НЕ имя аккаунта.** Правило подписи одно на доску, звонок и
   * списки, и считает его `shownNameOf` (`./user.ts`), четыре ступени. Первая
   * ступень это имя внутри группы, то есть чужие данные, поэтому считает
   * подпись сервер и отдаёт готовой строкой.
   *
   * Профильное имя из провайдера входа («Приходько Владислав») уехать сюда не
   * может ни при каких условиях: это ровно та утечка, которую чинили правкой
   * 23.08.2026 (`docs/room-feedback-fixes.md`, раздел 4), и канал списка
   * ничем не лучше канала токена LiveKit, где она и жила.
   */
  shownAs: z.string(),
});
export type RoomParticipant = z.infer<typeof RoomParticipant>;

/**
 * Состав комнаты целиком.
 *
 * ⚠️ **Объект со списком, а не голый массив.** Массив верхним уровнем не
 * переживёт добавления ни одного поля: дописать рядом со списком нечего, и
 * первое же «а сколько всего» или «а когда посчитано» потребует ломающей
 * правки контракта у обеих сторон сразу.
 *
 * ⚠️ **Порядок строк задаёт сервер, и он устойчив между запросами** (по
 * подписи, совпадения разрываются идентификатором). Список рисуется на экране,
 * и перескакивающие строки это ошибка: преподаватель метит в кнопку рядом с
 * одним ребёнком, а нажимает рядом с другим. Пересортировывать его на клиенте
 * поэтому не надо.
 */
export const RoomRosterResponse = z.object({
  participants: z.array(RoomParticipant),
});
export type RoomRosterResponse = z.infer<typeof RoomRosterResponse>;
